import { useEffect, useMemo, useState } from 'react';
import {
  Body1Strong,
  Button,
  Caption1,
  Field,
  Link,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { InfoRegular, OpenRegular } from '@fluentui/react-icons';
import type { AppInsightsResource } from '@/services/applicationInsightsService';
import { useApplicationInsightsResources } from '@/hooks/useApplicationInsightsResources';
import { SearchableCombobox } from '@/components/shared';

const AZURE_PORTAL_CREATE_URL = 'https://portal.azure.com/#create/Microsoft.AppInsights';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  preview: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalM,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  previewMetaRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    color: tokens.colorNeutralForeground3,
  },
  previewConnection: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    wordBreak: 'break-all',
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
  },
  buttonRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  privateEndpointWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorPaletteYellowForeground1,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
    border: `1px dashed ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    alignItems: 'flex-start',
  },
});

interface AppInsightsResourcePickerProps {
  /** Pre-selected subscription id, e.g. remembered from localStorage. */
  initialSubscriptionId?: string;
  /** Fired whenever the user changes the chosen subscription. */
  onSubscriptionChange?: (subscriptionId: string) => void;
  /** Fired when the user clicks "Use this resource" to commit. */
  onConfirm: (resource: AppInsightsResource) => void;
  /** Disable the confirm button while the parent's mutation is in flight. */
  saving?: boolean;
}

/**
 * Pure-function filter helpers for the picker. Exported so they can be
 * regression-tested without coupling the test to Fluent v9 Combobox's
 * popover behavior, which is awkward to drive reliably under JSDOM.
 */
export const filterSubscriptionGroups = (
  groups: ReturnType<typeof useApplicationInsightsResources>['groups'],
  search: string,
) => {
  const q = search.trim().toLowerCase();
  if (!q) return groups;
  return groups.filter(
    (g) =>
      g.subscription.displayName.toLowerCase().includes(q) ||
      g.subscription.subscriptionId.toLowerCase().includes(q),
  );
};

export const filterResources = (resources: AppInsightsResource[], search: string) => {
  const q = search.trim().toLowerCase();
  if (!q) return resources;
  return resources.filter(
    (r) =>
      r.name.toLowerCase().includes(q) ||
      r.location.toLowerCase().includes(q) ||
      r.resourceGroup.toLowerCase().includes(q),
  );
};

export const AppInsightsResourcePicker = ({
  initialSubscriptionId,
  onSubscriptionChange,
  onConfirm,
  saving,
}: AppInsightsResourcePickerProps) => {
  const styles = useStyles();
  const { isLoading, isError, groups, totalResources, refetch } = useApplicationInsightsResources();

  const [subscriptionId, setSubscriptionIdState] = useState<string>(initialSubscriptionId ?? '');
  const [resourceId, setResourceId] = useState<string>('');

  const selectedGroup = useMemo(
    () => groups.find((g) => g.subscription.subscriptionId === subscriptionId) ?? null,
    [groups, subscriptionId],
  );

  const selectedResource = useMemo(
    () => selectedGroup?.resources.find((r) => r.id === resourceId) ?? null,
    [selectedGroup, resourceId],
  );

  // Auto-select first sub when groups arrive (or when the remembered sub isn't
  // in the result set anymore — e.g. user lost access since last visit).
  useEffect(() => {
    if (isLoading || isError || groups.length === 0) return;
    if (selectedGroup) return;
    const first = groups[0]!.subscription.subscriptionId;
    setSubscriptionIdState(first);
    setResourceId('');
    // intentional: react only to groups changing
  }, [groups, isLoading, isError, selectedGroup]);

  const setSubscriptionId = (next: string) => {
    setSubscriptionIdState(next);
    setResourceId('');
    onSubscriptionChange?.(next);
  };

  if (isLoading) {
    return (
      <Spinner
        size="tiny"
        labelPosition="after"
        label="Searching your subscriptions for Application Insights resources…"
      />
    );
  }

  if (isError) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>Couldn&apos;t list Application Insights resources</MessageBarTitle>
          Try again, or switch to &quot;Paste connection string&quot; to enter one manually.
        </MessageBarBody>
        <MessageBarActions>
          <Button onClick={() => refetch()}>Try again</Button>
        </MessageBarActions>
      </MessageBar>
    );
  }

  if (totalResources === 0) {
    return (
      <div className={styles.emptyState}>
        <Body1Strong>No Application Insights resources found</Body1Strong>
        <Caption1>
          You don&apos;t have any Application Insights resources in your subscriptions. Create one in the Azure portal, then refresh — or switch to &quot;Paste connection string&quot; to enter one manually.
        </Caption1>
        <div className={styles.buttonRow}>
          <Button
            as="a"
            href={AZURE_PORTAL_CREATE_URL}
            target="_blank"
            rel="noopener noreferrer"
            icon={<OpenRegular />}
            iconPosition="after"
          >
            Create in Azure portal
          </Button>
          <Button appearance="subtle" onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      </div>
    );
  }

  const subscriptionOptions = groups.map(({ subscription: sub }) => ({
    value: sub.subscriptionId,
    text: sub.displayName,
    secondaryText: sub.subscriptionId,
    searchTerms: [sub.subscriptionId],
  }));

  const resourceOptions = (selectedGroup?.resources ?? []).map((r) => ({
    value: r.id,
    text: r.name,
    secondaryText: `${r.location} · ${r.resourceGroup}`,
    searchTerms: [r.location, r.resourceGroup],
  }));

  return (
    <div className={styles.root}>
      <Field label="Azure subscription" required>
        <SearchableCombobox
          options={subscriptionOptions}
          value={subscriptionId || undefined}
          onChange={setSubscriptionId}
          placeholder="Select or type to filter"
          aria-label="Azure subscription"
          noMatchMessage="No matching subscriptions"
        />
      </Field>

      <Field label="Application Insights resource" required>
        <SearchableCombobox
          options={resourceOptions}
          value={resourceId || undefined}
          onChange={setResourceId}
          disabled={!selectedGroup}
          placeholder={selectedGroup ? 'Select or type to filter' : 'Select a subscription first'}
          aria-label="Application Insights resource"
          noMatchMessage="No matching resources"
        />
      </Field>

      {selectedResource && (
        <div className={styles.preview} aria-label="Selected resource preview">
          <Body1Strong>{selectedResource.name}</Body1Strong>
          {selectedResource.hasPrivateEndpoint && (
            <Caption1 className={styles.privateEndpointWarning}>
              <InfoRegular aria-hidden />
              Private endpoint — telemetry from Otto apps may not reach this resource unless they share a network path.
            </Caption1>
          )}
          <div className={styles.previewMetaRow}>
            <Caption1>Region: {selectedResource.location}</Caption1>
            <Caption1>Resource group: {selectedResource.resourceGroup}</Caption1>
          </div>
          <Caption1>Connection string</Caption1>
          <div className={styles.previewConnection}>{selectedResource.connectionString}</div>
          <div className={styles.buttonRow}>
            <Button
              appearance="primary"
              onClick={() => onConfirm(selectedResource)}
              disabled={!!saving}
            >
              {saving ? 'Saving…' : 'Use this resource'}
            </Button>
            <Link
              href={`https://portal.azure.com/#resource${selectedResource.id}/overview`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Azure portal
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};
